<template>
    <div class="wechat-login-container">
        <!-- 二维码显示区域 -->
        <div class="qr-section">
            <!-- 微信官方二维码容器 -->
            <div :id="containerId" class="wechat-qr-container"></div>

            <!-- 加载状态覆盖层 -->
            <div v-if="isLoading" class="loading-overlay">
                <div class="loading-spinner">
                    <div class="spinner-ring"></div>
                    <div class="loading-text">加载中...</div>
                </div>
            </div>

            <!-- 状态提示 -->
            <div v-if="statusText && !isLoading" class="status-tip">
                {{ statusText }}
            </div>

            <!-- 错误提示 -->
            <div v-if="error && !isLoading" class="error-tip">
                {{ error }}
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import { createWechatLogin } from '@/utils/wechatLogin';
import { ElMessage } from 'element-plus';

export default {
    name: 'WechatLoginQR',
    emits: ['login-success', 'login-failed', 'status-change'],
    setup(props, { emit }) {
        const containerId = ref(`wechat-login-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
        const isLoading = ref(true);
        const error = ref('');
        const statusText = ref('');
        const isInitialized = ref(false);

        let wechatLogin = null;

        // 初始化微信登录
        const initWechatLogin = async () => {
            if (isInitialized.value || wechatLogin) {
                console.log('微信登录已初始化，跳过重复初始化');
                return;
            }

            try {
                isInitialized.value = true;
                error.value = '';
                console.log('开始初始化微信登录，容器ID:', containerId.value);

                // 等待DOM更新
                await nextTick();

                // 检查容器是否存在
                const container = document.getElementById(containerId.value);
                console.log('查找容器结果:', container);

                if (!container) {
                    throw new Error(`未找到二维码容器 (ID: ${containerId.value})`);
                }

                if (!wechatLogin) {
                    wechatLogin = createWechatLogin();

                    wechatLogin.setOnLoginSuccess((userData) => {
                        statusText.value = '登录成功';
                        emit('login-success', userData);
                    });

                    wechatLogin.setOnLoginFailed((errorMsg) => {
                        error.value = errorMsg;
                        emit('login-failed', errorMsg);
                    });

                    wechatLogin.setOnStatusChange((status, data) => {
                        updateStatus(status, data);
                        emit('status-change', status, data);
                    });
                }

                console.log('开始调用wechatLogin.init');
                // 使用微信官方的深色模式，并隐藏标题
                await wechatLogin.init(containerId.value, {
                    style: 'black',
                    color_scheme: 'dark',
                    href: createHideStyleCSS()
                });

                statusText.value = '';
                console.log('微信登录初始化完成');

            } catch (err) {
                console.error('微信登录初始化失败:', err);
                error.value = err.message || '初始化失败';
                ElMessage.error(error.value);
            } finally {
                isLoading.value = false;
            }
        };

        // 创建隐藏标题的CSS样式
        const createHideStyleCSS = () => {
            const cssContent = `
                /* 隐藏所有标题相关元素 */
                .impowerBox .title,
                .impowerBox .info,
                .impowerBox .status,
                .impowerBox .status_txt,
                .impowerBox .wxqrcode .title,
                .impowerBox .wxqrcode .info,
                .impowerBox .wxqrcode .status,
                .status_icon,
                .impowerBox .status_icon,
                .impowerBox .wxqrcode .status_icon {
                    display: none !important;
                    visibility: hidden !important;
                    opacity: 0 !important;
                    height: 0 !important;
                    width: 0 !important;
                    overflow: hidden !important;
                }
                
                /* 设置容器尺寸 */
                .impowerBox {
                    text-align: center;
                    width: 240px !important;
                    height: 240px !important;
                    overflow: hidden !important;
                }
                
                /* 设置二维码尺寸 */
                .impowerBox .qrcode {
                    margin: 0 auto;
                    width: 240px !important;
                    height: 240px !important;
                    display: block !important;
                }
                
                /* 强制隐藏所有非二维码内容 */
                .impowerBox *:not(.qrcode):not(.qrcode *) {
                    display: none !important;
                }
                
                /* 确保二维码图片正常显示 */
                .impowerBox .qrcode img {
                    display: block !important;
                    width: 100% !important;
                    height: 100% !important;
                }
            `;

            const blob = new Blob([cssContent], { type: 'text/css' });
            return URL.createObjectURL(blob);
        };

        // 更新状态
        const updateStatus = (status, data) => {
            switch (status) {
                case 'ready':
                    statusText.value = '';
                    break;
                case 'scanning':
                    statusText.value = '请在手机上确认';
                    break;
                case 'confirmed':
                    statusText.value = '登录成功';
                    break;
                case 'success':
                    statusText.value = '登录成功';
                    // 延迟清理，让用户看到成功提示
                    setTimeout(() => {
                        if (wechatLogin) {
                            wechatLogin.destroy();
                            wechatLogin = null;
                        }
                    }, 1000);
                    break;
                case 'expired':
                    error.value = '二维码已过期';
                    break;
                case 'error':
                    error.value = data?.message || '登录失败';
                    break;
                default:
                    statusText.value = '';
            }
        };

        // 组件挂载时自动初始化
        onMounted(async () => {
            // 等待DOM完全渲染
            await nextTick();
            // 直接初始化
            initWechatLogin();
        });

        // 组件销毁时清理
        onUnmounted(() => {
            console.log('组件销毁，清理微信登录');
            if (wechatLogin) {
                wechatLogin.destroy();
                wechatLogin = null;
            }
            isInitialized.value = false;
        });

        return {
            containerId,
            isLoading,
            error,
            statusText
        };
    }
};
</script>

<style scoped>
.wechat-login-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    min-height: 300px;
}

/* 二维码部分 */
.qr-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
}

.wechat-qr-container {
    width: 240px;
    height: 240px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
}

/* 加载状态 */
.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(15, 23, 42, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
}

.loading-spinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.spinner-ring {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(255, 255, 255, 0.15);
    border-top-color: #3b82f6;
    border-left-color: #60a5fa;
    border-radius: 50%;
    animation: spin 1.2s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

.loading-text {
    color: rgba(255, 255, 255, 0.95);
    font-size: 14px;
    font-weight: 500;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    animation: pulse 1.5s ease-in-out infinite;
    letter-spacing: 0.5px;
}

@keyframes pulse {
    0%, 100% {
        opacity: 0.8;
    }
    50% {
        opacity: 1;
    }
}

/* 状态提示 */
.status-tip {
    font-size: 14px;
    color: #60a5fa;
    text-align: center;
    margin: 0;
    font-weight: 500;
    padding: 8px 16px;
    background: rgba(16, 185, 129, 0.1);
    border-radius: 6px;
    border: 1px solid rgba(16, 185, 129, 0.2);
    animation: slideIn 0.3s ease;
}

.error-tip {
    font-size: 14px;
    color: #ef4444;
    text-align: center;
    margin: 0;
    font-weight: 500;
    padding: 8px 16px;
    background: rgba(239, 68, 68, 0.1);
    border-radius: 6px;
    border: 1px solid rgba(239, 68, 68, 0.2);
    animation: slideIn 0.3s ease;
}

@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* 响应式 */
@media (max-width: 480px) {
    .wechat-login-container {
        padding: 16px;
    }

    .wechat-qr-container {
        width: 200px;
        height: 200px;
    }
}
</style>